import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Server, 
  Database, 
  Globe, 
  Play, 
  RotateCcw, 
  Square, 
  ArrowLeft, 
  Settings, 
  Activity,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';

const AdminPanel = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState({
    pm2: { status: 'unknown', loading: false },
    apache2: { status: 'unknown', loading: false },
    mysql: { status: 'unknown', loading: false }
  });
  const [lastAction, setLastAction] = useState('');
  const [isValidAdmin, setIsValidAdmin] = useState(false);

  useEffect(() => {
    // Check if this is a valid admin session - completely isolated from main app
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      // Only allow access if this is specifically an admin token
      if (payload.type === 'admin') {
        setIsValidAdmin(true);
        checkServiceStatuses();
      } else {
        // Not an admin token, redirect to home
        navigate('/');
      }
    } catch (error) {
      console.error('Invalid admin token:', error);
      navigate('/');
    }
  }, [navigate]);

  const checkServiceStatuses = async () => {
    // Placeholder - will implement API calls later
    console.log('Checking service statuses...');
    setServices(prev => ({
      pm2: { ...prev.pm2, status: 'active' },
      apache2: { ...prev.apache2, status: 'active' },
      mysql: { ...prev.mysql, status: 'active' }
    }));
  };

  const handleServiceAction = async (service, action) => {
    setServices(prev => ({
      ...prev,
      [service]: { ...prev[service], loading: true }
    }));

    setLastAction(`${action.charAt(0).toUpperCase() + action.slice(1)}ing ${service}...`);

    // Simulate API call delay
    setTimeout(() => {
      setServices(prev => ({
        ...prev,
        [service]: { 
          ...prev[service], 
          loading: false,
          status: action === 'stop' ? 'inactive' : 'active'
        }
      }));
      setLastAction(`${service} ${action}ed successfully`);
    }, 2000);
  };

  const getStatusIcon = (status, loading) => {
    if (loading) return <Clock className="w-5 h-5 text-yellow-400 animate-spin" />;
    
    switch (status) {
      case 'active':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'inactive':
        return <AlertCircle className="w-5 h-5 text-red-400" />;
      default:
        return <Activity className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-400';
      case 'inactive':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  const ServiceCard = ({ service, icon: Icon, title, description }) => {
    const serviceData = services[service];
    
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Icon className="w-6 h-6 text-blue-400 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-slate-200">{title}</h3>
              <p className="text-sm text-slate-400">{description}</p>
            </div>
          </div>
          <div className="flex items-center">
            {getStatusIcon(serviceData.status, serviceData.loading)}
            <span className={`ml-2 text-sm font-medium ${getStatusColor(serviceData.status)}`}>
              {serviceData.loading ? 'Processing...' : serviceData.status}
            </span>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={() => handleServiceAction(service, 'start')}
            disabled={serviceData.loading}
            className="flex items-center justify-center bg-green-600 hover:bg-green-700 
                     disabled:bg-gray-600 disabled:cursor-not-allowed
                     text-white font-medium py-3 px-4 rounded-md transition-colors"
          >
            <Play className="w-4 h-4 mr-1" />
            Start
          </button>
          
          <button
            onClick={() => handleServiceAction(service, 'restart')}
            disabled={serviceData.loading}
            className="flex items-center justify-center bg-yellow-600 hover:bg-yellow-700 
                     disabled:bg-gray-600 disabled:cursor-not-allowed
                     text-white font-medium py-3 px-4 rounded-md transition-colors"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Restart
          </button>
          
          <button
            onClick={() => handleServiceAction(service, 'stop')}
            disabled={serviceData.loading}
            className="flex items-center justify-center bg-red-600 hover:bg-red-700 
                     disabled:bg-gray-600 disabled:cursor-not-allowed
                     text-white font-medium py-3 px-4 rounded-md transition-colors"
          >
            <Square className="w-4 h-4 mr-1" />
            Stop
          </button>
        </div>
      </div>
    );
  };

  // Don't render anything until we verify admin access
  if (!isValidAdmin) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-slate-400">Verifying admin access...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Settings className="w-8 h-8 text-blue-400 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-slate-200">KSP Admin Panel</h1>
                <p className="text-slate-400">System Service Control</p>
              </div>
            </div>
            <button
              onClick={() => {
                // Clear admin session and redirect to login
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                navigate('/login');
              }}
              className="flex items-center bg-slate-700 hover:bg-slate-600 text-slate-200 
                       font-medium py-2 px-4 rounded-md transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Exit Admin
            </button>
          </div>
          
          {lastAction && (
            <div className="bg-blue-900/30 border border-blue-800 text-blue-300 px-4 py-2 rounded-md">
              {lastAction}
            </div>
          )}
        </div>

        {/* Service Controls */}
        <div className="space-y-4">
          <ServiceCard
            service="pm2"
            icon={Server}
            title="PM2 Process Manager"
            description="Controls the Express.js server process"
          />
          
          <ServiceCard
            service="apache2"
            icon={Globe}
            title="Apache2 Web Server"
            description="HTTP/HTTPS web server service"
          />
          
          <ServiceCard
            service="mysql"
            icon={Database}
            title="MySQL Database"
            description="Database management system"
          />
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-slate-500 text-sm">
            Logged in as: KSP Administrator
          </p>
          <p className="text-slate-600 text-xs mt-1">
            Admin Session Active
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;